import { useState, useEffect } from "react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Settings2, Pencil, FileText, Loader2, Check, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { useContracts } from "@/contexts/ContractContext";
import { Link } from "react-router-dom";
import { ConfigurationModal } from "@/components/ConfigurationModal";
import { generateAgreement, getAgreementsStatus } from "@/api/agreements";
import { getStatusBadgeClass, contractNeedsReview } from "@/utils";
import { ContractStatus } from "@/types";
import { useAuth } from "@/contexts/AuthContext";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

export function Pending() {
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const { contracts, updateContractStatus, fetchContracts } = useContracts();
  const [selectedContractId, setSelectedContractId] = useState<number | null>(null);
  const [isConfigOpen, setIsConfigOpen] = useState(false);
  const [selectedSettings, setSelectedSettings] = useState<{
    isTestSubmission: boolean;
    isWelcomeEmail: boolean;
    isSendWelcomeEmailDraft: boolean;
    isAttachJointMemberForm: boolean;
    isWithoutII: boolean;
    isWithoutIIGuaranteedReservation: boolean;
  }>({
    isTestSubmission: false,
    isWelcomeEmail: false,
    isSendWelcomeEmailDraft: false,
    isAttachJointMemberForm: false,
    isWithoutII: false,
    isWithoutIIGuaranteedReservation: false
  });
  // Store contract settings locally without updating the API
  const [contractSettings, setContractSettings] = useState<Record<number, {
    isTestSubmission: boolean;
    isWelcomeEmail: boolean;
    isSendWelcomeEmailDraft: boolean;
    isAttachJointMemberForm: boolean;
    isWithoutII: boolean;
    isWithoutIIGuaranteedReservation: boolean;
  }>>({});
  const [generatingAgreement, setGeneratingAgreement] = useState<number | null>(null);
  const [displayContracts, setDisplayContracts] = useState<any[]>([]);
  const { user } = useAuth();

  const refreshContracts = async () => {
    await fetchContracts(); // Re-fetch contracts to update state
  };

  // Filter contracts based on status - show only pending by default
  useEffect(() => {
    const filtered = contracts
      .filter(contract => contract.status === 'pending')
      .sort((a, b) => {
        const dateA = new Date(a.createdAt || Date.now()).getTime();
        const dateB = new Date(b.createdAt || Date.now()).getTime();
        return sortOrder === 'asc' ? dateA - dateB : dateB - dateA;
      });

    setDisplayContracts(filtered);
  }, [contracts, sortOrder]);

  const toggleSort = () => {
    setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
  };

  const handleConfigClick = (contractId: number) => {
    setSelectedContractId(contractId);

    // Get the contract's current settings from our local state or from the contract
    const savedSettings = contractSettings[contractId];
    const contract = contracts.find(c => c._id === contractId);
    const hasFormOfSurrender = (contract?.formOfSurrenders?.length ?? 0) > 0;

    if (savedSettings) {
      setSelectedSettings(hasFormOfSurrender
        ? { ...savedSettings, isWithoutII: false, isWithoutIIGuaranteedReservation: false }
        : savedSettings);
    } else if (contract) {
      
      setSelectedSettings({
        isTestSubmission: contract.isTestSubmission || false,
        isWelcomeEmail: false,
        isSendWelcomeEmailDraft: false,
        isAttachJointMemberForm: false,
        isWithoutII: false,
        isWithoutIIGuaranteedReservation: false
      });

    } else {
      setSelectedSettings({
        isTestSubmission: false,
        isWelcomeEmail: false,
        isSendWelcomeEmailDraft: false,
        isAttachJointMemberForm: false,
        isWithoutII: false,
        isWithoutIIGuaranteedReservation: false
      });
    }

    setIsConfigOpen(true);
  };

  const handleSettingsChange = (type: 'isTestSubmission' | 'isWelcomeEmail' | 'isSendWelcomeEmailDraft' | 'isAttachJointMemberForm' | 'isWithoutII' | 'isWithoutIIGuaranteedReservation') => {
    setSelectedSettings(prev => {
      // If disabling welcome email, also disable welcome email draft and attach joint member form
      if (type === 'isWelcomeEmail' && prev.isWelcomeEmail) {
        return {
          ...prev,
          [type]: !prev[type],
          isSendWelcomeEmailDraft: false,
          isAttachJointMemberForm: false,
          isWithoutII: false,
          isWithoutIIGuaranteedReservation: false
        };
      }
      // Only allow toggling isSendWelcomeEmailDraft or isAttachJointMemberForm if isWelcomeEmail is true
      if ((type === 'isSendWelcomeEmailDraft' || type === 'isAttachJointMemberForm') && !prev.isWelcomeEmail) {
        return prev;
      }
      // isWithoutII and isWithoutIIGuaranteedReservation are mutually exclusive modes
      if (type === 'isWithoutII') {
        return { ...prev, isWithoutII: !prev.isWithoutII, isWithoutIIGuaranteedReservation: false };
      }
      if (type === 'isWithoutIIGuaranteedReservation') {
        return { ...prev, isWithoutIIGuaranteedReservation: !prev.isWithoutIIGuaranteedReservation, isWithoutII: false };
      }
      return {
        ...prev,
        [type]: !prev[type]
      };
    });
  };

  const handleSaveSettings = async () => {
    if (selectedContractId) {
      // Store settings locally instead of updating the contract in the API
      setContractSettings(prev => ({
        ...prev,
        [selectedContractId]: { ...selectedSettings }
      }));

      toast.success("Settings saved successfully");
      setIsConfigOpen(false);

      await refreshContracts(); // Refresh contracts after saving settings
    }
  };

  const handleCreateAgreement = async () => {
    if (!selectedContractId) return;

    try {
      setGeneratingAgreement(selectedContractId);

      // Call the generateAgreement function with the new parameters
      const response = await generateAgreement(
        selectedContractId,
        selectedSettings.isTestSubmission,
        selectedSettings.isWelcomeEmail,
        selectedSettings.isSendWelcomeEmailDraft,
        selectedSettings.isAttachJointMemberForm,
        selectedSettings.isWithoutII,
        selectedSettings.isWithoutIIGuaranteedReservation
      );

      setGeneratingAgreement(null);

      if (response && response.success) {
        // Update contract status to active when agreement is generated successfully
        updateContractStatus(selectedContractId, ContractStatus.Active, {
          adobeStatus: 'waiting_for_prefill'
        });

        toast.success(response.message || "Agreement generated successfully");
        setIsConfigOpen(false);

        await refreshContracts(); // Refresh contracts after generating agreement
      } else {
        if (response?.errors) {
          // Handle validation errors
          if (typeof response.errors === 'object' && response.errors !== null) {
            // Extract error messages from the object
            const errorMessages: string[] = [];
            Object.entries(response.errors).forEach(([field, messages]) => {
              if (Array.isArray(messages)) {
                messages.forEach(message => {
                  errorMessages.push(`${field}: ${message}`);
                });
              } else if (typeof messages === 'string') {
                errorMessages.push(`${field}: ${messages}`);
              }
            });

            if (errorMessages.length > 0) {
              // Show each validation error as a separate toast
              errorMessages.forEach(message => {
                toast.error(message);
              });
            } else {
              // If not successful, show error message
              toast.error(response.message || "Failed to generate agreement");
            }
          } else if (Array.isArray(response.errors)) {
            // Handle array of errors
            response.errors.forEach(message => {
              toast.error(message);
            });
          } else if (typeof response.errors === 'string') {
            // Handle string error
            toast.error(response.errors);
          }
        } else {
          // If not successful, show error message
          toast.error(response.message || "Failed to generate agreement");
        }
      }
    } catch (error: any) {
      toast.error(error.message || "Failed to generate agreement");
      setGeneratingAgreement(null);
    }
  };

  const handleGenerateAgreement = async (contractId: number) => {
    try {
      // Check if we have settings for this contract in our local state
      const settings = contractSettings[contractId];

      // If settings aren't configured, open the configuration modal
      if (!settings) {
        handleConfigClick(contractId);
        return;
      }

      setGeneratingAgreement(contractId);

      // Call the generateAgreement function
      const response = await generateAgreement(
        contractId,
        settings.isTestSubmission,
        settings.isWelcomeEmail,
        settings.isSendWelcomeEmailDraft,
        settings.isAttachJointMemberForm,
        settings.isWithoutII,
        settings.isWithoutIIGuaranteedReservation
      );

      setGeneratingAgreement(null);

      if (response && response.success) {
        // Update contract status to active when agreement is generated successfully
        updateContractStatus(contractId, ContractStatus.Active, {
          adobeStatus: 'waiting_for_prefill'
        });

        toast.success(response.message || "Agreement generated successfully");

        await refreshContracts(); // Refresh contracts after generating agreement
      } else {
        if (response?.errors) {
          // Handle validation errors
          if (typeof response.errors === 'object' && response.errors !== null) {
            // Extract error messages from the object
            const errorMessages: string[] = [];
            Object.entries(response.errors).forEach(([field, messages]) => {
              if (Array.isArray(messages)) {
                messages.forEach(message => {
                  errorMessages.push(`${field}: ${message}`);
                });
              } else if (typeof messages === 'string') {
                errorMessages.push(`${field}: ${messages}`);
              }
            });

            if (errorMessages.length > 0) {
              // Show each validation error as a separate toast
              errorMessages.forEach(message => {
                toast.error(message);
              });
            } else {
              // If not successful, show error message
              toast.error(response.message || "Failed to generate agreement");
            }
          } else if (Array.isArray(response.errors)) {
            // Handle array of errors
            response.errors.forEach(message => {
              toast.error(message);
            });
          } else if (typeof response.errors === 'string') {
            // Handle string error
            toast.error(response.errors);
          }
        } else {
          // If not successful, show error message
          toast.error(response.message || "Failed to generate agreement");
        }
      }
    } catch (error: any) {
      toast.error(error.message || "Failed to generate agreement");
      setGeneratingAgreement(null);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold">Contracts</h1>
        <Button onClick={refreshContracts} variant="outline">
          Refresh
        </Button>
      </div>

      <Card className="overflow-hidden">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="cursor-pointer" onClick={toggleSort}>
                  Date {sortOrder === 'asc' ? '↑' : '↓'}
                </TableHead>
                <TableHead>Owner ID</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {displayContracts.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                    No pending contracts found
                  </TableCell>
                </TableRow>
              ) : (
                displayContracts.map((contract) => (
                  <TableRow key={contract._id}>
                    <TableCell>
                      {new Date(contract.createdAt || Date.now()).toLocaleDateString()}
                    </TableCell>
                    <TableCell>{contract.owner?.membershipNumber || 'N/A'}</TableCell>
                    <TableCell>
                      {`${contract.owner?.title || ''} ${contract.owner?.firstName || ''} ${contract.owner?.lastName || ''}`}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Badge variant="secondary" className={getStatusBadgeClass(contract.status || 'new')}>
                          {contract.status || 'New'}
                        </Badge>
                        {contractNeedsReview(contract) && (
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span>
                                  <AlertCircle className="h-4 w-4 text-amber-500" />
                                </span>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>Requires review</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex space-x-2">
                        <Link
                          to={`/contracts/${contract._id}`}
                          className="inline-flex items-center justify-center rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 hover:bg-accent hover:text-accent-foreground h-9 w-9"
                        >
                          <Pencil className="h-4 w-4" />
                        </Link>
                        <Button
                          variant="outline"
                          size="icon"
                          onClick={() => handleConfigClick(contract._id)}
                          title="Configure agreement settings"
                          disabled={generatingAgreement === contract._id}
                        >
                          <Settings2 className="h-4 w-4" />
                        </Button>
                        {contract.status === 'pending' && (
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <div>
                                  <Button
                                    size="sm"
                                    onClick={() => handleGenerateAgreement(contract._id)}
                                    className="bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700"
                                    disabled={
                                      generatingAgreement === contract._id ||
                                      contractNeedsReview(contract)
                                    }
                                  >
                                    {generatingAgreement === contract._id ? (
                                      <>
                                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                        Generating...
                                      </>
                                    ) : contractNeedsReview(contract) ? (
                                      <>
                                        <AlertCircle className="h-4 w-4 mr-2" />
                                        Needs Review
                                      </>
                                    ) : (
                                      <>
                                        <FileText className="h-4 w-4 mr-2" />
                                        Create Agreement
                                      </>
                                    )}
                                  </Button>
                                </div>
                              </TooltipTrigger>
                              {contractNeedsReview(contract) && (
                                <TooltipContent>
                                  <p>This contract requires review by an admin before generating an agreement</p>
                                </TooltipContent>
                              )}
                            </Tooltip>
                          </TooltipProvider>
                        )}
                        {contract.status === 'active' && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-green-600 border-green-600"
                            disabled
                          >
                            <Check className="h-4 w-4 mr-2" />
                            Generated
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <ConfigurationModal
        isOpen={isConfigOpen}
        onClose={() => setIsConfigOpen(false)}
        contractId={selectedContractId || undefined}
        settings={selectedSettings}
        onSettingsChange={handleSettingsChange}
        onSave={handleSaveSettings}
        onCreateAgreement={handleCreateAgreement}
        isGenerating={generatingAgreement === selectedContractId}
        hasFormOfSurrender={(contracts.find(c => c._id === selectedContractId)?.formOfSurrenders?.length ?? 0) > 0}
      />
    </div>
  );
}